// @ts-nocheck
import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';

import piexif from 'piexifjs';

// Components
import { Button } from '../../../components/UISignatue/Button';
import {
  ContainerButton,
  HeaderCam,
  Image,
  TitleCam,
  TitleButton,
  ActionCaptureSelfie,
  Mask,
  FooterCam,
} from './styles';

// Context
import { useSignature } from '../../../hooks/SignatureContext';
// Svgs
import { ArrowRight, ArrowLeft, ActionSelfie } from './svgs';

// Service
import SignatureService from '../../../services/SignatureService';
import { useRetentionSignature } from '../../../hooks/RetentionSignatureContext';

// Utils
import logger from '../../../utils/logger.utils';

const videoConstraints = { height: { max: 360 }, facingMode: 'user' };

const blobToDataURL = (blob: Blob) =>
  new Promise<string>((res) => {
    const fr = new FileReader();
    fr.onloadend = () => res(fr.result as string);
    fr.readAsDataURL(blob);
  });

const shrinkToJpeg = async (blob: Blob, max = 960, q = 0.9) => {
  const img = await createImageBitmap(blob);
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = img.width * scale;
  canvas.height = img.height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Falha ao gerar JPEG'));
      },
      'image/jpeg',
      q,
    ),
  );
};

const degToDmsRational = (deg: number) => {
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const mFloat = (abs - d) * 60;
  const m = Math.floor(mFloat);
  const sFloat = (mFloat - m) * 60;
  const s = Math.round(sFloat * 100);
  return [
    [d, 1],
    [m, 1],
    [s, 100],
  ];
};

const getCoords = (): Promise<{ lat: number | null; lng: number | null }> =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { timeout: 7000 },
    );
  });

/* Helpers EXIF */
const pad2 = (n: number) => String(n).padStart(2, '0');
const formatEXIFDate = (dt: Date) =>
  [dt.getFullYear(), pad2(dt.getMonth() + 1), pad2(dt.getDate())].join(':') +
  ' ' +
  [pad2(dt.getHours()), pad2(dt.getMinutes()), pad2(dt.getSeconds())].join(':');

const buildExif = async (lat: number | null, lng: number | null, photoSettings: any = {}) => {
  const nowStr = formatEXIFDate(new Date());
  const author = 'Banco Bari';

  const zeroth: any = {
    [piexif.ImageIFD.Make]: /Mobile/i.test(navigator.userAgent) ? 'Mobile-Browser' : 'Desktop-Browser',
    [piexif.ImageIFD.Model]: navigator.userAgent,
    [piexif.ImageIFD.Rating]: 5,
    [piexif.ImageIFD.Artist]: author,
    [piexif.ImageIFD.Copyright]: '(c) ' + author,
    [piexif.ImageIFD.DateTime]: nowStr,
  };

  const exifIfd: any = {
    [piexif.ExifIFD.ExifVersion]: '0232',
    [piexif.ExifIFD.DateTimeOriginal]: nowStr,
    [piexif.ExifIFD.DateTimeDigitized]: nowStr,
    [piexif.ExifIFD.FNumber]: photoSettings.aperture || [19, 10],
    [piexif.ExifIFD.ExposureTime]: photoSettings.exposureTime || [1, 60],
    [piexif.ExifIFD.ISOSpeedRatings]: photoSettings.iso > 0 ? photoSettings.iso : 100,
    [piexif.ExifIFD.FocalLength]: photoSettings.focalLength || undefined,
    [piexif.ExifIFD.MeteringMode]: photoSettings.meteringMode || undefined,
    [piexif.ExifIFD.SubjectDistance]: photoSettings.subjectDistance || undefined,
    [piexif.ExifIFD.Flash]: photoSettings.flashMode || undefined,
    [piexif.ExifIFD.FlashEnergy]: photoSettings.flashEnergy || undefined,
  };

  const gpsIfd: any =
    lat != null && lng != null
      ? {
          [piexif.GPSIFD.GPSLatitude]: degToDmsRational(lat),
          [piexif.GPSIFD.GPSLatitudeRef]: lat > 0 ? 'N' : 'S',
          [piexif.GPSIFD.GPSLongitude]: degToDmsRational(lng),
          [piexif.GPSIFD.GPSLongitudeRef]: lng > 0 ? 'E' : 'W',
        }
      : {};

  return { '0th': zeroth, Exif: exifIfd, GPS: gpsIfd };
};

// Aguarda o vídeo ter pelo menos um frame renderizado antes de capturar.
// Guarda defensiva: sem isso, drawImage() em canvas pode retornar frame preto.
const waitForVideoReady = (video: HTMLVideoElement, maxWaitMs = 3000): Promise<void> =>
  new Promise((resolve) => {
    if (video.readyState >= 2 && video.videoWidth > 0) return resolve();
    const start = Date.now();
    const check = () => {
      if (video.readyState >= 2 && video.videoWidth > 0) return resolve();
      if (Date.now() - start >= maxWaitMs) return resolve(); // não bloqueia indefinidamente
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });

// Nº de frames renderizados aguardados até a auto-exposição da câmera convergir.
// No iOS a exposição estabiliza por frame, não por tempo de relógio: em pouca luz
// o framerate cai, então contar frames se auto-ajusta à condição de iluminação.
const WARMUP_FRAMES = 30;
const WARMUP_FALLBACK_MS = 1000;

// Libera a captura só depois que a câmera "aqueceu" (exposição convergida).
// Em navegadores com requestVideoFrameCallback (Safari iOS 15.4+, Chrome, etc.)
// conta frames reais; senão cai num timeout fixo.
const warmupCamera = (video: HTMLVideoElement | undefined, onReady: () => void) => {
  if (!video) return onReady();

  const rvfc = (video as any).requestVideoFrameCallback?.bind(video);
  if (typeof rvfc === 'function') {
    let frames = 0;
    const tick = () => {
      frames += 1;
      if (frames >= WARMUP_FRAMES) return onReady();
      rvfc(tick);
    };
    rvfc(tick);
  } else {
    setTimeout(onReady, WARMUP_FALLBACK_MS);
  }
};

// Luminância média (0-255) abaixo da qual consideramos a foto escura demais.
// Ajustável: valores maiores = mais sensível a fotos escuras.
const DARK_LUMA_THRESHOLD = 55;

// Analisa o brilho médio da imagem (luma Rec. 601) usando uma amostra reduzida
// para não pesar no device. Nunca lança: em erro, retorna false (não bloqueia envio).
const isImageTooDark = (dataUrl: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const sampleWidth = 64;
        const scale = sampleWidth / Math.max(1, img.width);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(false);

        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);

        let lumaSum = 0;
        const pixels = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          lumaSum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        }

        resolve(lumaSum / pixels < DARK_LUMA_THRESHOLD);
      } catch {
        resolve(false);
      }
    };
    img.onerror = () => resolve(false);
    img.src = dataUrl;
  });

const Camera: React.FC = ({ formValues, isRetention = false }: any) => {
  const { setStep, setFormOpened, setShowModal, setErrorCam, formOpened } = isRetention
    ? useRetentionSignature()
    : useSignature();
  const webcamRef = useRef<any>(null);

  const [imgBase64, setImgBase64] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [isDark, setIsDark] = useState(false);

  // Aplica a imagem capturada e sinaliza (sem bloquear) se ela ficou escura demais.
  const finalizeCapture = useCallback(async (dataUrl: string) => {
    setImgBase64(dataUrl);
    const dark = await isImageTooDark(dataUrl);
    setIsDark(dark);
    if (dark) {
      logger.warn('Selfie capturada escura', { userAgent: navigator.userAgent });
    }
  }, []);

  const retakePhoto = useCallback(() => {
    setImgBase64('');
    setIsDark(false);
  }, []);

  const capturePhoto = useCallback(async () => {
    const video = webcamRef.current?.video;
    if (video) {
      await waitForVideoReady(video);
    }

    try {
      const track = webcamRef.current?.video?.srcObject?.getVideoTracks?.()[0];
      // @ts-ignore
      if (track && window.ImageCapture && typeof createImageBitmap === 'function') {
        // @ts-ignore
        const imgCap = new ImageCapture(track);
        const raw = await imgCap.takePhoto();
        const jpeg = await shrinkToJpeg(raw);
        const data = await blobToDataURL(jpeg);
        let photoSettings = {};
        try {
          photoSettings = await imgCap.getPhotoSettings();
        } catch {}

        const { lat, lng } = await getCoords();
        const withExif = piexif.insert(piexif.dump(await buildExif(lat, lng, photoSettings)), data);
        return finalizeCapture(withExif);
      }
    } catch (e) {
      logger.error('ImageCapture falhou, usando fallback getScreenshot', {
        userAgent: navigator.userAgent,
        error: e,
      });
    }

    const frame = webcamRef.current?.getScreenshot();
    if (!frame) return;

    const { lat, lng } = await getCoords();
    finalizeCapture(piexif.insert(piexif.dump(await buildExif(lat, lng, {})), frame));
  }, [finalizeCapture]);

  const handlePrevstep = () => {
    setStep((previousStep: any) => previousStep - 1);
    setShowModal(false);
  };

  const sendPhoto = async () => {
    if (!imgBase64) return;
    setLoading(true);
    try {
      const [, payload] = imgBase64.split('base64,');
      if (!payload) throw new Error('Base64 não encontrado');

      const resp = await SignatureService.sendSelfie({
        token: formOpened.token,
        base64: payload,
        isRetentionPortability: isRetention,
      });
      formValues.selfieId = resp.data.Id;
      setFormOpened(formValues);
      setStep((previousStep: any) => previousStep + 1);
    } catch (err) {
      logger.error('Erro ao enviar selfie', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {imgBase64 ? (
        <>
          <HeaderCam>
            <button onClick={handlePrevstep}>
              <ArrowLeft />
            </button>
            <TitleCam>Selfie</TitleCam>
          </HeaderCam>
          <Image>
            <img src={imgBase64 || ''} alt="Screenshot" />
          </Image>
          <ContainerButton>
            <TitleButton>A foto ficou boa?</TitleButton>
            {isDark && (
              <p style={{ color: '#d64545', fontSize: 14, textAlign: 'center', margin: '4px 0 12px' }}>
                A foto parece escura. Procure um local mais iluminado e tire novamente para melhorar a qualidade.
              </p>
            )}
            <Button black big spaced onClick={sendPhoto} iconRight={ArrowRight} {...{ disabled: '' || loading }}>
              {loading ? 'Enviando...' : 'Enviar foto'}
            </Button>
            <Button transparent big spaced onClick={retakePhoto}>
              {imgBase64 ? 'Tirar uma nova foto' : 'Foto não capturada'}
            </Button>
          </ContainerButton>
        </>
      ) : (
        <>
          <Mask>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              screenshotQuality={1}
              onUserMediaError={() => setErrorCam(true)}
              onUserMedia={() => warmupCamera(webcamRef.current?.video, () => setVideoReady(true))}
            />
            <ActionCaptureSelfie>
              <button className="action-selfie" onClick={capturePhoto} disabled={!videoReady}>
                <ActionSelfie />
              </button>
            </ActionCaptureSelfie>
          </Mask>
          <FooterCam>
            <p>Enquadre seu rosto na moldura e fotografe</p>
          </FooterCam>
        </>
      )}
    </>
  );
};

export default Camera;
